import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  getNotificationTemplateList,
  isActiveNotificationTemplate
} from "../../redux/services/notificationTemplate.service";
import { toast } from "react-toastify";
import { showError } from "../../components/common/commonAlert";
import { customStylesForGrid } from "../../constants/helper";
import { Button } from "react-bootstrap";
import CommonDataTable from "../../components/common/CommonDataTable";
import { useDebouncedCallback } from "../../constants/useDebouncedCallback";

const NotificationTemplateList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [record, setRecord] = useState([]);
  const [totalRecord, setTotalRecord] = useState([]);

  const [notificationTemplateReqModel, setNotificationTemplateReqModel] = useState({
    SearchText: "",
    SortCol: "notificationtemplateid",
    SortDir: "asc",
    PageNo: 1,
    PageSize: 10
  });

  const updateRequestModel = useCallback((key, value) => {
    setNotificationTemplateReqModel((prev) => ({
      ...prev,
      [key]: value
    }));
  }, []);

  const notificationTemplateSearchValueChange = useCallback(
    useDebouncedCallback((searchValue) => {
      updateRequestModel("SearchText", searchValue);
      updateRequestModel("PageNo", 1);
    }, 1000),
    [updateRequestModel]
  );
  /** Pagination change */
  const handlePageNo = useCallback(
    (page) => {
      updateRequestModel("PageNo", page);
      setPageNo(page);
    },
    [updateRequestModel]
  );

  const handlePageSizeChange = useCallback(
    (perPage) => {
      updateRequestModel("PageSize", perPage);
      //updateRequestModel("PageNo", 1);
      setPageSize(perPage);
    },
    [updateRequestModel]
  );

  const handleSort = useCallback((columnName) => {
    if (!columnName) return;
    setNotificationTemplateReqModel((prev) => ({
      ...prev,
      SortCol: columnName,
      SortDir: prev.SortDir === "asc" ? "desc" : "asc"
    }));
  }, []);

  useEffect(() => {
    const fetchNotificationTemplate = async () => {
      setLoading(true);
      try {
        const startRow =
          notificationTemplateReqModel.PageSize * (notificationTemplateReqModel.PageNo - 1);
        const qry = `?searchText=${notificationTemplateReqModel?.SearchText || ""}&sortCol=${notificationTemplateReqModel?.SortCol || ""}&sortDir=${notificationTemplateReqModel?.SortDir || ""}&startRow=${startRow}&pageSize=${notificationTemplateReqModel?.PageSize}`;
        const response = await getNotificationTemplateList(qry);
        setRecord(response?.data.data);
        setTotalRecord(response?.data.recordsTotal);
      } catch (error) {
        console.error("Error fetching Notifiction templates:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchNotificationTemplate();
  }, [notificationTemplateReqModel]);

  const columns = [
    {
      name: "Notification Name",
      selector: (row) => row.notificationName,
      sortable: true,
      columnName: "notificationName"
    },

    {
      name: "Notification Key",
      sortable: true,
      columnName: "notificationKey",
      cell: (row) => row.notificationKey
    },
    {
      name: "Notification URL",
      columnName: "notificationURL",
      selector: (row) => row.notificationURL
    },

    {
      name: "Notification Type",
      sortable: true,
      columnName: "notificationType",
      cell: (row) => row.notificationType
    },
    {
      name: "Notification Text",
      sortable: true,
      columnName: "notificationText",
      cell: (row) => {
        const plainText = row.notificationText.replace(/<[^>]+>/g, "");
        const html =
          plainText.length > 40
            ? `${row.notificationText.substring(0, 40)}...`
            : row.notificationText;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: html
            }}
          />
        );
      }
    },

    {
      name: "Description",
      sortable: true,
      columnName: "description",
      cell: (row) =>
        row.description?.length > 40 ? `${row.description.substring(0, 40)}...` : row.description
    },
    {
      name: (
        <div style={{ whiteSpace: "normal", textAlign: "center" }}>
          Active /
          <br />
          Inactive
        </div>
      ),
      center: true,
      cell: (row) =>
        row ? (
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              style={{ fontSize: "20px" }}
              type="checkbox"
              checked={row.isActive}
              title={row.isActive ? "Active" : "Inactive"}
              onChange={(e) => handleUserActionToggle(row.notificationTemplateId, e.target.checked)}
            />
          </div>
        ) : null
    },
    {
      name: "Actions",
      cell: (row) => <EditButtonTemplate row={row} />,
      button: true,
      width: "100px"
    }
  ];

  const handleUserActionToggle = async (id, value) => {
    isActiveNotificationTemplate(id, value)
      .then((response) => {
        if (response.statusCode === 200) {
          toast.success(
            `Notification Template ${value ? "Activated" : "Deactivated"} Successfully!`,
            {
              autoClose: 1000
            }
          );
        }
        setRecord((prev) =>
          prev.map((item) =>
            item.notificationTemplateId === id ? { ...item, isActive: value } : item
          )
        );
      })
      .catch((error) => showError(error));
  };

  const EditButtonTemplate = ({ row }) => {
    return (
      <>
        {row.isActive && (
          <>
            <button
              type="button"
              className="btn btn-sm btn-icon dropdown-toggle custom-dropdown-toggle hide-arrow"
              data-bs-toggle="dropdown">
              <i className="bi bi-plus"></i>
            </button>
            <ul className="dropdown-menu custom-dropdown-menu dropdown-menu-end m-0">
              <li>
                <Link
                  to={`/notificationtemplate/edit/${row.notificationTemplateId}`}
                  title="Edit"
                  className="dropdown-item">
                  <i className="bi bi-pencil-square text-primary"></i>
                </Link>
              </li>
            </ul>
          </>
        )}
      </>
    );
  };
  return (
    <>
      <div className="row">
        <div className="col-md-8">
          <h2>Notification Templates</h2>
        </div>

        <div className="col-md-4 text-md-end">
          <Button className="btn btn-secondary" onClick={() => navigate(-1)}>
            <i className="pi pi-arrow-left"></i> Back
          </Button>
        </div>
      </div>

      <div className="card">
        <div className="card-body px-4">
          <div className="row">
            <div className="col-md-8">
              <Link to="/notificationtemplate/add" className="btn icon icon-left btn-primary">
                <i className="pi pi-plus"></i> Add Notification Template
              </Link>
            </div>

            <div className="col-md-4">
              <div className="input-group mb-3">
                <input
                  type="text"
                  className="form-control"
                  onChange={(e) => notificationTemplateSearchValueChange(e.target.value)}
                  placeholder="Search by any field..."
                />
                <span className="input-group-text pb-3">
                  <i className="pi pi-search"></i>
                </span>
              </div>
            </div>
            <hr />
          </div>

          <div className="container p-0 m-0 row mw-100">
            <CommonDataTable
              columns={columns}
              data={record}
              loading={loading}
              customStyles={customStylesForGrid}
              pageNo={pageNo}
              pageSize={pageSize}
              recordTotal={totalRecord}
              handlePageNo={handlePageNo}
              handlePageSizeChange={handlePageSizeChange}
              handleSort={handleSort}
              enablePagination={true}
            />
          </div>
        </div>
      </div>
    </>
  );
};
export default NotificationTemplateList;
